/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import { AvatarGroup, Box, Button, Card, CardHeader, Checkbox, Divider, FormControlLabel, MenuItem, Pagination, Select, Stack, Typography, useTheme } from '@mui/material';
import hljs from 'highlight.js';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import Avatar from '../../../components/Avatar';
import ConfirmDialog from '../../../components/ConfirmDialog';
import EmptyContent from '../../../components/EmptyContent';
import { SocialShare } from '../../../components/FBSocial';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';
import Markdown from '../../../components/Markdown';
import { SkeletonCommentItem } from '../../../components/skeleton/SkeletonItem';
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import { setQuestionSearch } from '../../../redux/slices/blog/blog.question';
import { findCommentAPI } from '../../../service/blog/blog.comment.service';
import { deleteQuestionAPI } from '../../../service/blog/blog.question.service';
import { getStatisticAPI, updateLikeAPI } from '../../../service/blog/blog.statistic.service';
import createAvatar from '../../../utils/createAvatar';
import { fShortenNumber } from '../../../utils/formatNumber';
import { getString } from '../../../utils/urlSlug';
import { CommentItemWrapper } from '../../comment/CommentItem';
import CommentNewForm from '../../comment/CommentNewForm';
import QuestionEditForm from './QuestionEditForm';

// ----------------------------------------------------------------------
QuestionCard.propTypes = {
  question: PropTypes.object,
};

export default function QuestionCard({ question }) {
  const theme = useTheme();
  const { translate } = useLocales();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { user, isAuthenticated } = useAuth();
  const { search } = useSelector((state) => state.blogQuestion);

  const [isShowComments, setIsShowComments] = useState(true);
  const [isShowReplies, setIsShowReplies] = useState(false);
  const [statistic, setStatistic] = useState({ viewNo: 0, likeNo: 0, commentNo: 0, likers: [] });
  const [open, setOpen] = useState(false);
  const [editable, setEditable] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      getStatistic();
      hljs.highlightAll();
    }, 500);
    return () => clearTimeout(timeout);
  }, [question?.id]);

  const handleSelectTag = (value) => {
    let tags = [value];
    const tagIds = tags?.map((t) => t.id).toString();
    dispatch(setQuestionSearch({ ...search, page: 0, tags, filterBys: { ...search.filterBys, tagIds } }));
  };

  const confirmDeleteItem = async () => {
    let resp = await deleteQuestionAPI(question?.id);

    setOpen(false);
    if (resp.code === "200") {
      enqueueSnackbar(translate('message.deleteSuccess'), { variant: 'success' });
      dispatch(setQuestionSearch({ ...search }));
    } else
      enqueueSnackbar(`${resp.code} - ${resp.message}`, { variant: 'error' });
  };

  const handleEdit = async () => {
    setEditable(true);
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      enqueueSnackbar(translate('label.signinToGPLX'), { variant: 'error' });
      return;
    }
    const { code } = await updateLikeAPI(question?.statistic.id);
    code === '200' && getStatistic();
  };

  const getStatistic = async () => {
    const { code, data } = await getStatisticAPI(question?.statistic.id);
    code === '200' && setStatistic(data);
  };

  return (
    <Card>
      <CardHeader
        disableTypography
        avatar={<Avatar
          src={question?.createdBy?.photoURL}
          alt={question?.createdBy?.displayName}
          color={question?.createdBy?.photoURL ? 'default' : createAvatar(question?.createdBy?.displayName).color}
        >
          {createAvatar(question?.createdBy?.displayName).name}
        </Avatar>}
        title={
          <Typography variant="subtitle2" color="text.primary" noWrap sx={{ cursor: 'pointer' }}>
            {question?.createdBy?.displayName}
          </Typography>
        }
        subheader={
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {(question.createdAt)}
          </Typography>
        }
        action={
          <Stack direction={"row"} spacing={1} >
            {user?.id === question?.createdBy.id && (
              <Button onClick={handleEdit} size='small' color='primary' variant='text' startIcon={<Iconify icon="circum:edit" sx={{ height: 18, width: 18 }} />}>
                {translate("button.edit")}
              </Button>)}
            {user?.id === question?.createdBy.id && (
              <Button size='small' color='error' variant='text' startIcon={<Iconify icon="octicon:trash-16" sx={{ height: 18, width: 18 }} />}
                onClick={() => setOpen(true)}>
                {translate("button.delete")}
              </Button>)}
          </Stack>
        }
      />

      <Stack spacing={1} sx={{ px: 3, py: 1 }}>
        {editable ?
          <QuestionEditForm id={question?.id} qTags={question?.tags} content={question?.description} isEdit={true} setEditable={setEditable} />
          : <>
            <Box>
              {question?.tags?.map((tag) => (
                <Label key={tag.id} sx={{ mr: 1, cursor: 'pointer' }} onClick={() => handleSelectTag(tag)}>{tag.title}</Label>
              ))}
            </Box>
            <div className='ql-snow'>
              <div className='ql-editor' style={{ padding: 0 }}>
                <Markdown children={question?.description} />
              </div>
            </div>
            <SocialShare title={getString(question?.description)} url={`https://onthibanglaixe.net/questions/${question?.id}`} />
            <Divider />
          </>}
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent={"space-between"}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button color='inherit' variant='text' size='small'
              startIcon={<Iconify icon="humbleicons:chat" sx={{ height: 18, width: 18 }} />}
              sx={{ ...theme.typography.body2 }}
              onClick={() => setIsShowComments(!isShowComments)}>
              {isShowComments ? 'Ẩn' : 'Hiện'} {translate("button.comment").toLowerCase()} ({statistic?.commentNo})
            </Button>
            <Button color='inherit' variant='text' size='small'
              startIcon={<Iconify icon="bi:reply" sx={{ height: 18, width: 18 }} />}
              sx={{ ...theme.typography.body2 }}
              onClick={() => setIsShowReplies(!isShowReplies)}>
              {translate("button.reply")}
            </Button>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  defaultChecked
                  onChange={handleLike}
                  size="small"
                  color="error"
                  icon={<Iconify icon="eva:heart-fill" />}
                  checkedIcon={<Iconify icon="eva:heart-fill" />}
                />
              }
              label={fShortenNumber(statistic?.likeNo)}
            />
            <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 22, height: 22 }, }} >
              {statistic?.likers?.map((person) => (
                <Avatar key={person.displayName} alt={person.displayName} src={person.photoURL} />
              ))}
            </AvatarGroup>
          </Box>
        </Stack>
        {isShowReplies && <CommentNewForm itemId={question?.id} type={"QUESTION"} />}
        {isShowComments && <CommentList itemId={question?.id} />}
      </Stack>
      <ConfirmDialog values={{ title: translate("message.dialogDeleteTitle"), content: translate("message.dialogDeleteContent") }}
        onClose={() => setOpen(false)} isOpen={open} onSubmit={confirmDeleteItem} />
    </Card>
  );
}

CommentList.propTypes = {
  itemId: PropTypes.number.isRequired,
};

function CommentList({ itemId }) {
  const { translate } = useLocales();

  const { search: searchComment } = useSelector((state) => state.blogComment);
  const [sortBy, setSortBy] = useState("0");

  let [search, setSearch] = useState({
    page: 0,
    size: 10,
    value: '%%',
    orders: [{ order: "desc", property: 'createdAt' }],
    filterBys: { itemId, type: "QUESTION" }
  });

  let [response, setResponse] = useState({
    isLoading: false,
    totalPages: 0,
    numberOfElements: 0,
    comments: [],
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      getComments();
    }, 500);
    return () => clearTimeout(timeout);
  }, [search, itemId, searchComment]);

  const getComments = async () => {
    setResponse({ ...response, isLoading: true });

    const { code, data: comments, totalPages, numberOfElements } = await findCommentAPI(search);

    if (code === '200')
      setResponse({ ...response, comments, totalPages, numberOfElements, isLoading: false });
    else
      setResponse({ ...response, isLoading: false });
  };

  const handleChangePage = (_, value) => {
    setSearch({ ...search, page: value - 1 });
  };

  const handleChangeSort = (event) => {
    let value = event.target.value;

    let orders = [{ order: "desc", property: 'createdAt' }];

    if (value === "0") orders = [{ order: "desc", property: 'createdAt' }];
    else if (value === "1") orders = [{ order: "asc", property: 'createdAt' }];
    else orders = [{ order: "desc", property: 'likeNo' }];

    setSearch({ ...search, page: 0, orders });
    setSortBy(value);
  };

  return (<>
    <Divider />
    <Stack my={1} spacing={2}>
      <Box>
        <Typography variant='caption'>{translate("label.sortBy")}: </Typography>
        <Select variant='standard' value={sortBy} size='small' sx={{ fontSize: 12, fontWeight: 'bold', ml: 1, pt: 1 }} disableUnderline
          onChange={handleChangeSort} >
          <MenuItem value={"0"} sx={{ fontSize: 12 }}>{translate("label.sortByNew")}</MenuItem>
          <MenuItem value={"1"} sx={{ fontSize: 12 }} >{translate("label.sortByOld")}</MenuItem>
          <MenuItem value={"2"} sx={{ fontSize: 12 }}>{translate("label.sortByLike")}</MenuItem>
        </Select>
      </Box>
      <Stack spacing={2}>
        {response?.comments.map((comment) => (
          <CommentItemWrapper comment={comment} key={comment.id} />
        ))}
        {response?.isLoading ? <SkeletonCommentItem /> :
          response?.numberOfElements == 0 && <EmptyContent title=" " description={translate("label.noComment")} />}
      </Stack>
      {response?.numberOfElements > 0 && <Stack alignItems={"center"} my={1.5}>
        <Pagination count={response?.totalPages} variant='text' page={search.page + 1} onChange={handleChangePage} />
      </Stack>}
    </Stack>
  </>
  );
}
